import React, { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import PageHero from "@/components/PageHero";
import Section from "@/components/Section";
import api from "@/lib/api";
import { CheckCircle2, Bed, Calendar, ShieldCheck, Building2, ArrowRight, Send } from "lucide-react";

const benefits = [
  { icon: Bed, title: "Receive bed bookings", desc: "SOS and scheduled bed bookings flow into your hospital queue in real time." },
  { icon: Calendar, title: "Manage personnel calendars", desc: "Your doctors, physiotherapists and dieticians manage free and occupied slots in-app." },
  { icon: ShieldCheck, title: "Verified listing", desc: "Your Clinical Establishments registration is verified before going live — trust by default." },
  { icon: Building2, title: "You stay in control", desc: "Final hospital billing remains entirely with you. HomeCure never quotes hospital prices to patients." },
];

export default function ForHospitals() {
  const [form, setForm] = useState({
    hospital_name: "", contact_person: "", designation: "", email: "", phone: "",
    city: "", state: "", registration_number: "", bed_count: "", message: ""
  });
  const [submitting, setSubmitting] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const payload = { ...form };
      payload.bed_count = payload.bed_count ? Number(payload.bed_count) : null;
      Object.keys(payload).forEach(k => { if (payload[k] === "") payload[k] = null; });
      const res = await api.post("/partnership/hospital", payload);
      toast.success(res.data?.message || "Submitted!");
      setForm({ hospital_name: "", contact_person: "", designation: "", email: "", phone: "", city: "", state: "", registration_number: "", bed_count: "", message: "" });
    } catch (err) {
      const msg = err?.response?.data?.detail || "Could not submit. Please try again.";
      toast.error(typeof msg === "string" ? msg : "Could not submit.");
    } finally { setSubmitting(false); }
  };

  return (
    <>
      <PageHero
        title="For Hospitals"
        subtitle="Receive SOS and scheduled bed bookings, manage doctor/physio/dietician calendars and offer video consultations — all from one platform."
      />

      <Section eyebrow="Why partner with HomeCure" title="Built for hospitals — not against them" center>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {benefits.map((b, i) => (
            <div key={i} className="hc-card p-6">
              <div className="w-11 h-11 rounded-lg bg-[#E8F0FB] flex items-center justify-center text-[#3780E0]">
                <b.icon className="w-5 h-5" />
              </div>
              <div className="mt-3 font-bold text-lg text-[#0F2A4A]">{b.title}</div>
              <div className="mt-1 text-sm text-slate-600">{b.desc}</div>
            </div>
          ))}
        </div>
      </Section>

      <Section bg="light" eyebrow="Onboarding" title="What we need to verify your hospital">
        <div className="grid md:grid-cols-2 gap-6">
          <div className="hc-card p-6 bg-white">
            <ul className="space-y-2 text-sm text-slate-700">
              <li className="flex gap-2"><CheckCircle2 className="w-4 h-4 mt-0.5 text-[#3780E0]" /> Hospital Registration Number under Clinical Establishments Act, 2010</li>
              <li className="flex gap-2"><CheckCircle2 className="w-4 h-4 mt-0.5 text-[#3780E0]" /> State / district licensing authority details</li>
              <li className="flex gap-2"><CheckCircle2 className="w-4 h-4 mt-0.5 text-[#3780E0]" /> Authorised signatory details (PAN, contact)</li>
              <li className="flex gap-2"><CheckCircle2 className="w-4 h-4 mt-0.5 text-[#3780E0]" /> List of departments, bed categories and operational specialities</li>
              <li className="flex gap-2"><CheckCircle2 className="w-4 h-4 mt-0.5 text-[#3780E0]" /> Empanelled doctors, physios and dieticians to be listed</li>
              <li className="flex gap-2"><CheckCircle2 className="w-4 h-4 mt-0.5 text-[#3780E0]" /> Banking details for settlement &amp; GST (if applicable)</li>
              <li className="flex gap-2"><CheckCircle2 className="w-4 h-4 mt-0.5 text-[#3780E0]" /> Accreditation details (NABH, JCI) if any</li>
            </ul>
          </div>
          <div className="hc-card p-6 bg-white">
            <div className="font-bold text-[#0F2A4A] text-lg">How payments flow</div>
            <ul className="mt-2 space-y-2 text-sm text-slate-700">
              <li className="flex gap-2"><CheckCircle2 className="w-4 h-4 mt-0.5 text-[#3780E0]" /> Patients pay full consultation fees online through the platform; settled to your hospital via gateway</li>
              <li className="flex gap-2"><CheckCircle2 className="w-4 h-4 mt-0.5 text-[#3780E0]" /> Bed-booking commission is HomeCure&apos;s facilitation fee — not yours</li>
              <li className="flex gap-2"><CheckCircle2 className="w-4 h-4 mt-0.5 text-[#3780E0]" /> Final hospital bill is paid directly to your hospital by the patient</li>
              <li className="flex gap-2"><CheckCircle2 className="w-4 h-4 mt-0.5 text-[#3780E0]" /> Hospitals do <strong>not</strong> pay a commission to HomeCure</li>
            </ul>
          </div>
        </div>
      </Section>

      <Section eyebrow="Get started" title="Tell us about your hospital">
        <form onSubmit={submit} className="hc-card p-6 md:p-8 max-w-3xl mx-auto" data-testid="hospital-inquiry-form">
          <div className="grid md:grid-cols-2 gap-4">
            <div><label className="hc-label">Hospital name *</label><input required value={form.hospital_name} onChange={set("hospital_name")} className="hc-input" data-testid="hosp-name" /></div>
            <div><label className="hc-label">Contact person *</label><input required value={form.contact_person} onChange={set("contact_person")} className="hc-input" data-testid="hosp-person" /></div>
            <div><label className="hc-label">Designation</label><input value={form.designation} onChange={set("designation")} className="hc-input" /></div>
            <div><label className="hc-label">Email *</label><input required type="email" value={form.email} onChange={set("email")} className="hc-input" data-testid="hosp-email" /></div>
            <div><label className="hc-label">Phone *</label><input required value={form.phone} onChange={set("phone")} className="hc-input" data-testid="hosp-phone" /></div>
            <div><label className="hc-label">City *</label><input required value={form.city} onChange={set("city")} className="hc-input" data-testid="hosp-city" /></div>
            <div><label className="hc-label">State</label><input value={form.state} onChange={set("state")} className="hc-input" /></div>
            <div><label className="hc-label">Registration No. (CEA / state)</label><input value={form.registration_number} onChange={set("registration_number")} className="hc-input" /></div>
            <div><label className="hc-label">Approx. bed count</label><input type="number" min="0" value={form.bed_count} onChange={set("bed_count")} className="hc-input" /></div>
          </div>
          <div className="mt-4"><label className="hc-label">Anything else we should know?</label><textarea rows={4} value={form.message} onChange={set("message")} className="hc-input" /></div>
          <button type="submit" disabled={submitting} className="hc-btn-primary mt-5 disabled:opacity-60" data-testid="hosp-submit">
            <Send className="w-4 h-4" /> {submitting ? "Submitting…" : "Submit inquiry"}
          </button>
        </form>
      </Section>

      <Section bg="primary" center>
        <h2 className="text-white text-3xl md:text-4xl font-extrabold">Looking to onboard your hospital chain?</h2>
        <p className="text-white/90 mt-3">We can support multi-location partnerships and bulk personnel onboarding.</p>
        <div className="mt-6 flex justify-center gap-3 flex-wrap">
          <Link to="/contact" className="bg-white text-[#2563B3] hover:bg-slate-100 font-bold px-6 py-3 rounded-lg inline-flex items-center gap-2">Speak to our team <ArrowRight className="w-4 h-4" /></Link>
        </div>
      </Section>
    </>
  );
}
